/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { Grid } from "@cd2/components"
import { Fields } from "@cd2/forms"
import { useTheme } from "@cd2/theme"
import React from "react"
import { useProductGroupRebates } from "../../hooks/useProductRebates"
import { sum } from "../../utils/arrays"
import { getCurrencyOption } from "../../utils/currency"
import { getTotals } from "../../utils/getQuoteOrderTotals"
import { useSettings } from "../../utils/useSettings"
import { formatPrice } from "../../utils/utils"
import {
  calculateMarginFromPrice,
  IItem,
  IQuoteOrderRecord,
  isDeleted,
  itemTypes,
} from "./quoteOrderUtils"
import { useGlobalState } from "../../Components/GlobalStateProvider"

interface IProps {
  quote: IQuoteOrderRecord

  onCarriagePriceChange(newPrice: IQuoteOrderRecord["carriage_pennies"]): void
  isCustomerViewing?: boolean
}

export function QuoteTotals({ quote, onCarriagePriceChange, isCustomerViewing }: IProps) {
  const { showDiscountOption } = useSettings()
  const { currentUser } = useGlobalState()
  const currency =
    getCurrencyOption(quote?.currency_name || currentUser?.branch.currency_name || "pound sterling")
      ?.symbol ?? ""
  const productRebates = useProductGroupRebates({
    customerId: quote?.customer?.id,
    productIds: quote?.items
      .map(item => (isDeleted(item) ? null : item.product?.id))
      .filter(Boolean),
  })

  const {
    subTotalPennies,
    discountPennies,
    branchDiscountPennies,
    vatPennies,
    totalPricePennies,
    rebateAmountPennies,
  } = getTotals(
    quote.items
      .map(item => {
        if (!isDeleted(item) && item.type !== itemTypes.buildSetItem) {
          return {
            product: item.product,
            price_pennies: item.price_pennies,
            quantity: item.quantity,
            discountPercent: item.discountPercent,
            opportunity_certificate_cost_discount_amount_pennies:
              item.opportunity_certificate_cost_discount_amount_pennies,
          }
        }
      })
      .filter(Boolean),
    quote.carriage_pennies,
    quote.customer?.tax_rate ?? 20,
    productRebates,
  )

  const theme = useTheme()
  const labelContainerCss = css`
    padding: 16px;
    background-color: #ddd;
    font-weight: 600;
  `

  return (
    <div
      css={css`
        overflow: hidden;
        background-color: #f5f5f5;
        border-radius: ${theme.borderRadius}px;
      `}
    >
      <Grid columns={[{ fr: 1 }, { fr: 2 }]}>
        <div css={labelContainerCss}>Carriage</div>
        <div
          css={css`
            padding: 4px;
          `}
        >
          <Fields.Price
            key={(quote.customer?.id ?? "no-customer") + quote.dpdProduct?.id}
            value={quote.carriage_pennies}
            onChange={onCarriagePriceChange}
            inputProps={{ min: 0 }}
            prefix={currency}
            inPennies
          />
        </div>
        {[
          { label: "Subtotal", price: subTotalPennies },
          rebateAmountPennies && { label: "Rebate", price: rebateAmountPennies },
          !isCustomerViewing && {
            label: "Margin",
            price: (() => {
              const costPrice = sum(
                quote.items.filter(
                  item => !isDeleted(item) && item.type !== itemTypes.buildSetItem,
                ) as IItem[],
                item => (item.cost_price_pennies || 0) * (item.quantity ?? 0),
              )

              return `${calculateMarginFromPrice(
                costPrice,
                subTotalPennies - discountPennies - rebateAmountPennies,
              )}%`
            })(),
          },
          showDiscountOption && {
            label: "Customer Discount",
            price: discountPennies,
          },
          showDiscountOption && {
            label: "Subtotal incl Discount",
            price: subTotalPennies - discountPennies,
          },
          showDiscountOption && {
            label: "Branch Discount",
            price: branchDiscountPennies,
          },
          { label: "VAT", price: vatPennies },
          { label: "Total", price: totalPricePennies },
        ]
          .filter(Boolean)
          .map(({ label, price }) => (
            <React.Fragment key={label}>
              <div css={labelContainerCss}>{label}</div>
              <div
                css={css`
                  padding: 16px;
                `}
              >
                {typeof price === "string"
                  ? price
                  : formatPrice(price, { isPennies: true, currency })}
              </div>
            </React.Fragment>
          ))}
      </Grid>
    </div>
  )
}
